import { capitalize, getWords } from '@attain-apps/string-utils'
import { TypeChecker } from '@attain-apps/type-guards'
import type * as agentCore from '@aws-sdk/client-bedrock-agentcore'

import type { Message } from './message'

/** Retains the complete AWS Code Interpreter result in message history. */
// The interface intentionally extends the SDK contract so the domain model
// follows future additions without recreating or aliasing that contract.
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface CodeInterpreterResult
  extends agentCore.CodeInterpreterResult {}

/**
 * Additional information about the message, typically used when {@link Message.role} is `'agent'`
 * to capture reasoning steps within the message.
 *
 * This is the canonical message-information contract shared by agent
 * invocation, persistence, GraphQL, and the frontend.
 */
export type Info =
  | Info.Text
  | Info.Rationale
  | Info.Status
  | Info.Code
  | Info.CodeOutput
  | Info.CodeError
  | Info.ToolUse
  | Info.ToolResult
  | Info.Citation
  | Info.File
  | Info.Error
export namespace Info {
  interface Base {
    /** When this entry was created. */
    createdAt: Date
  }

  /** A chunk of LLM-generated text (one entry per streamed chunk). */
  export interface Text extends Base {
    type: 'text'
    text: string
  }
  export namespace Text {
    export const isText = TypeChecker.Factory.custom(
      (entry: Info) => entry.type === 'text',
      { typeDescription: 'Info.Text' },
    )
  }

  /** The model's internal reasoning step. */
  export interface Rationale extends Base {
    type: 'rationale'
    traceId: string
    text: string
  }

  /** A coarse lifecycle label (e.g. 'Thinking', 'Processing complete'). */
  export interface Status extends Base {
    type: 'status'
    traceId: string
    label: string
  }

  /** Source code submitted to the code interpreter. */
  export interface Code extends Base {
    type: 'code'
    traceId: string
    language: agentCore.ProgrammingLanguage
    code: string
  }

  /** Successful output from the code interpreter. */
  export interface CodeOutput extends Base {
    type: 'codeOutput'
    traceId: string
    /** Present for classic Bedrock; AgentCore retains the raw results instead. */
    output?: string
    /** Complete interpreter results retained for execution history. */
    results?: readonly CodeInterpreterResult[]
  }

  /** Error output from the code interpreter. */
  export interface CodeError extends Base {
    type: 'codeError'
    traceId: string
    /** Present when the invocation itself throws before returning results. */
    error?: string
    /** Results received before or as part of the failed execution. */
    results?: readonly CodeInterpreterResult[]
  }

  /** A tool (action group) invocation request. Pairs with {@link ToolResult} via `traceId`. */
  export interface ToolUse extends Base {
    type: 'toolUse'
    traceId: string
    toolName: string
    actionGroup?: string
    apiPath?: string
    function?: string
    input?: unknown
    status: 'invoking'
  }

  /** The response from a tool invocation. Pairs with {@link ToolUse} via `traceId`. */
  export interface ToolResult extends Base {
    type: 'toolResult'
    traceId: string
    output?: string
    error?: string
    status: 'completed' | 'failed'
  }
  export namespace ToolResult {
    export interface Normalized {
      outputData?: unknown
      outputText?: string
      error?: string
      retry?: { attempt: number; max: number }
      state: 'completed' | 'failed'
    }
    export namespace Normalized {
      export function from(entry: ToolResult): Normalized {
        const outputData = _parsePayload(entry.output)
        const outputText = _formatPayload(entry.output)
        const embeddedError = _extractEmbeddedError(entry.output)
        const retry = _extractRetryInfo(entry.output)
        const isFailed =
          entry.status === 'failed' || embeddedError !== undefined

        return {
          error: entry.error ?? embeddedError,
          outputData,
          outputText,
          retry,
          state: isFailed ? 'failed' : 'completed',
        }
      }
    }
  }

  /** A cited span from a knowledge-base retrieval. */
  export interface Citation extends Base {
    type: 'citation'
    /** The generated text span that was cited. */
    text: string
    references: readonly {
      url?: string
      title?: string
      content?: string
      sourceUri?: string
      metadata?: unknown
    }[]
  }
  export namespace Citation {
    export const isCitation = TypeChecker.Factory.custom(
      (entry: Info) => entry.type === 'citation',
      { typeDescription: 'Info.Citation' },
    )

    export type Reference = Citation['references'][number]

    export interface Source {
      id: string
      badgeLabel: string
      key: string
      label: string
    }
    export namespace Source {
      export function fromReference(ref: Reference, index: number): Source {
        const sourceLabel = Source.label(ref)

        return {
          badgeLabel: Source.badgeLabel(sourceLabel),
          id: `source-${index}`,
          key: Source.key(ref),
          label: sourceLabel,
        }
      }

      export function key(ref: Reference): string {
        const url = ref.url?.trim()
        if (url) {
          return `url:${url.toLowerCase()}`
        }
        const sourceUri = ref.sourceUri?.trim()
        if (sourceUri) {
          return `source:${_stripS3Bucket(sourceUri).toLowerCase()}`
        }
        const title = ref.title?.trim()
        if (title) {
          return `title:${title.toLowerCase()}`
        }
        const content = ref.content?.trim()
        if (content) {
          return `content:${content.slice(0, 256).toLowerCase()}`
        }
        return 'source:unknown'
      }

      export function label(ref: Reference): string {
        const title = ref.title?.trim()
        if (title) {
          return title
        }
        const sourceUri = ref.sourceUri?.trim()
        if (sourceUri) {
          const sourcePath = _stripS3Bucket(sourceUri)
          return _fileNameFromPath(sourcePath) || sourcePath || 'Source'
        }
        const url = ref.url?.trim()
        if (url) {
          return url
        }
        return 'Source'
      }

      export function badgeLabel(sourceLabel: string): string {
        const shortLabel = _shortSourceLabel(sourceLabel)
        const words = shortLabel.split(/\s+/).filter(word => word.length > 0)
        const firstWord = words[0]
        if (!firstWord) {
          return '?'
        }
        const secondWord = words[1]
        if (firstWord.length <= 3 && firstWord === firstWord.toUpperCase()) {
          return secondWord
            ? `${firstWord[0] ?? ''}${secondWord[0] ?? ''}`.toUpperCase()
            : firstWord.slice(0, 2).toUpperCase()
        }
        return firstWord.slice(0, 1).toUpperCase()
      }
    }

    export interface Display {
      text: string
      sources: readonly Source[]
    }
    export namespace Display {
      export function from(params: from.Params): Display {
        const sourcesByKey = new Map<string, Source>()
        const anchors: _CitationAnchor[] = []
        const occupiedRanges: _CitationRange[] = []
        let searchStart = 0

        for (const citation of params.citations) {
          const sources = _resolveCitationSources(
            citation.references,
            sourcesByKey,
          )
          if (sources.length === 0 || citation.text.trim() === '') {
            continue
          }

          const range = _findCitationRange(
            params.text,
            citation.text,
            searchStart,
            occupiedRanges,
          )
          if (!range) {
            // Keep unmatched sources in the drawer without inventing a marker.
            continue
          }

          occupiedRanges.push(range)
          searchStart = range.end
          anchors.push({
            ...range,
            marker: _renderCitationMarker(sources),
          })
        }

        return {
          sources: Array.from(sourcesByKey.values()),
          text: _insertCitationMarkers(params.text, anchors),
        }
      }
      export namespace from {
        export interface Params {
          citations: readonly Citation[]
          text: string
        }
      }
    }

    interface _CitationRange {
      start: number
      end: number
    }

    interface _CitationAnchor extends _CitationRange {
      marker: string
    }

    function _resolveCitationSources(
      refs: readonly Reference[],
      sourcesByKey: Map<string, Source>,
    ): Source[] {
      const sources: Source[] = []
      const seenKeys = new Set<string>()

      for (const ref of refs) {
        const key = Source.key(ref)
        if (seenKeys.has(key)) {
          continue
        }
        seenKeys.add(key)
        let source = sourcesByKey.get(key)
        if (!source) {
          source = Source.fromReference(ref, sourcesByKey.size + 1)
          sourcesByKey.set(key, source)
        }
        sources.push(source)
      }

      return sources
    }

    function _findCitationRange(
      text: string,
      citationText: string,
      searchStart: number,
      occupiedRanges: readonly _CitationRange[],
    ): _CitationRange | undefined {
      const exact = _findExactCitationRange(
        text,
        citationText,
        searchStart,
        occupiedRanges,
      )
      if (exact) {
        return exact
      }
      return _findWhitespaceInsensitiveCitationRange(
        text,
        citationText,
        searchStart,
        occupiedRanges,
      )
    }

    function _findExactCitationRange(
      text: string,
      citationText: string,
      searchStart: number,
      occupiedRanges: readonly _CitationRange[],
    ): _CitationRange | undefined {
      const starts = [searchStart, 0]
      for (const start of starts) {
        let index = text.indexOf(citationText, start)
        while (index >= 0) {
          const range = { end: index + citationText.length, start: index }
          if (!_overlapsAny(range, occupiedRanges)) {
            return range
          }
          index = text.indexOf(citationText, index + 1)
        }
      }
      return undefined
    }

    function _findWhitespaceInsensitiveCitationRange(
      text: string,
      citationText: string,
      searchStart: number,
      occupiedRanges: readonly _CitationRange[],
    ): _CitationRange | undefined {
      const trimmed = citationText.trim()
      if (trimmed === '') {
        return undefined
      }
      const pattern = trimmed.split(/\s+/).map(_escapeRegExp).join('\\s+')
      const regex = new RegExp(pattern, 'g')
      const matches = Array.from(text.matchAll(regex))
        .map(match => ({
          end: (match.index ?? 0) + match[0].length,
          start: match.index ?? 0,
        }))
        .filter(range => !_overlapsAny(range, occupiedRanges))

      return matches.find(range => range.start >= searchStart) ?? matches[0]
    }

    function _overlapsAny(
      range: _CitationRange,
      occupiedRanges: readonly _CitationRange[],
    ): boolean {
      return occupiedRanges.some(
        occupied => range.start < occupied.end && occupied.start < range.end,
      )
    }

    function _insertCitationMarkers(
      text: string,
      anchors: readonly _CitationAnchor[],
    ): string {
      const anchorsByEnd = new Map<number, string[]>()
      for (const anchor of anchors) {
        const insertionIndex = _getCitationInsertionIndex(text, anchor)
        const markers = anchorsByEnd.get(insertionIndex) ?? []
        markers.push(anchor.marker)
        anchorsByEnd.set(insertionIndex, markers)
      }
      const insertionPoints = Array.from(anchorsByEnd.keys()).sort(
        (a, b) => b - a,
      )
      let output = text
      for (const end of insertionPoints) {
        const marker = `&nbsp;${anchorsByEnd.get(end)?.join('') ?? ''}`
        output = `${output.slice(0, end)}${marker}${output.slice(end)}`
      }
      return output
    }

    function _getCitationInsertionIndex(
      text: string,
      anchor: _CitationAnchor,
    ): number {
      let index = anchor.end
      while (index > anchor.start && /\s/.test(text[index - 1] ?? '')) {
        index -= 1
      }
      return index
    }

    function _renderCitationMarker(sources: readonly Source[]): string {
      if (sources.length === 0) {
        return ''
      }

      const sourceDots = sources
        .map(source => {
          const label = _escapeHtml(source.label)
          const badge = _escapeHtml(source.badgeLabel)
          return (
            '<span class="atn-message-citation-dot" ' +
            `title="${label}" aria-label="${label}">${badge}</span>`
          )
        })
        .join('')

      return (
        '<span class="atn-message-citation-set" aria-label="Citation sources">' +
        sourceDots +
        '</span>'
      )
    }

    function _stripS3Bucket(uri: string): string {
      const stripped = uri.replace(/^s3:\/\/[^/]+\//i, '')
      try {
        return decodeURIComponent(stripped)
      } catch {
        return stripped
      }
    }

    function _fileNameFromPath(path: string): string {
      const normalized = path.replace(/\\/g, '/')
      return normalized.split('/').filter(Boolean).pop() ?? ''
    }

    function _shortSourceLabel(label: string): string {
      return label
        .replace(/\.[^.]+$/, '')
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    }

    function _escapeHtml(value: string): string {
      return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
    }

    function _escapeRegExp(text: string): string {
      return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    }
  }

  /** A file produced by the agent or attached by the user (name only). */
  export interface File extends Base {
    type: 'file'
    name: string
  }
  export namespace File {
    export const isFile = TypeChecker.Factory.custom(
      (entry: Info) => entry.type === 'file',
      { typeDescription: 'Info.File' },
    )
  }

  /** An error that terminated or degraded the stream. */
  export interface Error extends Base {
    type: 'error'
    message: string
    cause:
      | 'returnControl'
      | 'streamException'
      | 'bedrockException'
      | 'agentCoreException'
    details?: unknown
  }

  /**
   * Distributive `Omit<Info, 'createdAt'>` - the typed payload without
   * a timestamp. Useful when the caller hasn't yet decided when to stamp
   * the entry.
   */
  export type Payload = _DistributiveOmit<Info, 'createdAt'>

  type _DistributiveOmit<T, K extends keyof any> = T extends unknown
    ? Omit<T, K>
    : never

  /**
   * An {@link Info} with the message it belongs to and its place in that
   * message's list.
   */
  export interface Stored {
    messageId: Message['id']
    /** Place in the message's list, which is the order entries are read in. */
    seq: number
    entry: Info
  }
  export namespace Stored {
    /** Identifies an entry wherever entries are collected together. */
    export type Id = `${Message['id']}:${number}`

    /** @see {@link Stored.Id} */
    export function idOf(stored: Pick<Stored, 'messageId' | 'seq'>): Id {
      return `${stored.messageId}:${stored.seq}`
    }

    /** Orders entries the way their message reads. */
    export function bySeq(a: Stored, b: Stored): number {
      return a.seq - b.seq
    }

    /** Result of {@link merge}. */
    export interface Merged {
      /** The message's entries, up to the first missing one. */
      info: readonly Info[]
      /** Whether entries were held back behind a missing one. */
      gap: boolean
    }

    /**
     * Lays the entries published for a message over those it was read with,
     * each at its {@link Stored.seq}.
     */
    export function merge(
      listed: readonly Info[],
      published: readonly Stored[],
    ): Merged {
      const placed: (Info | undefined)[] = [...listed]
      for (const stored of published) {
        placed[stored.seq] = stored.entry
      }

      const end = placed.findIndex(entry => entry === undefined)

      return end === -1
        ? { gap: false, info: placed as Info[] }
        : { gap: true, info: placed.slice(0, end) as Info[] }
    }
  }

  /**
   * The list with the text each rationale restates removed.
   *
   * A runtime may stream prose before saying what it was, and then say it was
   * its account of its work. Both are recorded; this is the reading that shows
   * the prose once.
   */
  export function withoutSupersededText(
    info: readonly Info[],
  ): readonly Info[] {
    const kept: Info[] = []
    for (const entry of info) {
      if (entry.type === 'rationale') {
        while (kept.at(-1)?.type === 'text') {
          kept.pop()
        }
      }
      kept.push(entry)
    }
    return kept
  }

  /**
   * A grouped, display-ready reasoning entry. Mirrors the source
   * {@link Info} variants but collapses paired entries
   * (status open/close, tool use/result) into a single item.
   */
  export type DisplayStep =
    | { kind: 'text'; text: string }
    | { kind: 'rationale'; text: string }
    | {
        kind: 'status'
        label: string
        /** `active` while no follow-up status has arrived for the same trace. */
        state: 'active' | 'done'
      }
    | {
        kind: 'tool'
        headerLabel: string
        headerOperation?: string
        inputLabel?: string
        inputData?: unknown
        inputText?: string
        outputLabel?: string
        outputData?: unknown
        outputText?: string
        /** That the step failed, not why: see {@link toClientSafe}. */
        error?: string
        retry?: { attempt: number; max: number }
        state: 'invoking' | 'completed' | 'failed'
      }
    | {
        kind: 'citation'
        text: string
        references: Citation['references']
      }
    | { kind: 'file'; name: string }
    | { kind: 'error'; message: string }

  /**
   * Collapses a raw info stream into display-ready steps (pairs `status`
   * entries by traceId, merges each tool or code execution into one row, and
   * resolves dangling active states once the message has stopped streaming).
   */
  export function groupInfo(
    info: readonly Info[],
    messageStatus: Message.Status,
  ): readonly DisplayStep[] {
    const steps: DisplayStep[] = []
    // Index of the last `status` step we emitted, keyed by traceId - used
    // to flip its state from `active` to `done` when a follow-up arrives.
    const statusIdxByTrace = new Map<string, number>()
    // Index of the `tool` step we emitted for a given traceId - used to
    // merge the matching `toolResult` into the existing entry.
    const toolIdxByTrace = new Map<string, number>()
    // Code Interpreter emits the submitted code and its results as separate
    // events. Keep their shared trace ID so the UI can present one execution
    // card instead of exposing each event as a separate reasoning step.
    const codeIdxByTrace = new Map<string, number>()

    for (const entry of withoutSupersededText(info)) {
      switch (entry.type) {
        case 'status': {
          const prevIdx = statusIdxByTrace.get(entry.traceId)
          if (prevIdx !== undefined) {
            // Mark the previous status for this trace as completed and replace
            // its label with the newer one (e.g. "Thinking" -> "Processing
            // complete").
            const prev = steps[prevIdx]
            if (prev?.kind === 'status') {
              prev.state = 'done'
              prev.label = entry.label
              continue
            }
          }
          statusIdxByTrace.set(entry.traceId, steps.length)
          steps.push({ kind: 'status', label: entry.label, state: 'active' })
          break
        }
        case 'toolUse': {
          toolIdxByTrace.set(entry.traceId, steps.length)
          const { headerLabel, headerOperation } = _composeToolHeader(
            entry.toolName,
            entry.actionGroup,
            entry.apiPath,
          )
          steps.push({
            kind: 'tool',
            headerLabel,
            headerOperation,
            inputData: _parsePayload(entry.input),
            inputText: _formatPayload(entry.input),
            state: 'invoking',
          })
          break
        }
        case 'toolResult': {
          const normalized = ToolResult.Normalized.from(entry)
          const idx = toolIdxByTrace.get(entry.traceId)
          if (idx !== undefined) {
            const existing = steps[idx]
            if (existing?.kind === 'tool') {
              existing.state = normalized.state
              existing.outputData = normalized.outputData
              existing.outputText = normalized.outputText
              existing.error = normalized.error
              existing.retry = normalized.retry
              break
            }
          }
          // Orphan result (no matching use) - surface it standalone so the
          // user still sees the outcome and any failure reason.
          steps.push({
            kind: 'tool',
            headerLabel: 'Tool result',
            outputData: normalized.outputData,
            outputText: normalized.outputText,
            error: normalized.error,
            retry: normalized.retry,
            state: normalized.state,
          })
          break
        }
        case 'text':
          steps.push({ kind: 'text', text: entry.text })
          break
        case 'rationale': {
          // Narration arrives one entry per token, so consecutive entries join
          // onto the step already open for them: one readable step that grows
          // as the prose streams, rather than hundreds of one-word rows.
          const previous = steps[steps.length - 1]
          if (previous?.kind === 'rationale') {
            previous.text += entry.text
          } else {
            steps.push({ kind: 'rationale', text: entry.text })
          }
          break
        }
        case 'code':
          codeIdxByTrace.set(entry.traceId, steps.length)
          steps.push({
            headerLabel: 'Code execution',
            headerOperation: _humanize(entry.language),
            inputLabel: 'Code',
            inputText: entry.code,
            kind: 'tool',
            outputLabel: 'Output',
            state: 'invoking',
          })
          break
        case 'codeOutput': {
          const output =
            entry.output ?? _codeInterpreterResultsAsText(entry.results)
          const idx = codeIdxByTrace.get(entry.traceId)
          const execution = idx === undefined ? undefined : steps[idx]
          if (execution?.kind === 'tool') {
            execution.outputText = _appendText(execution.outputText, output)
            execution.state = 'completed'
            break
          }
          steps.push({
            headerLabel: 'Code execution',
            kind: 'tool',
            outputLabel: 'Output',
            outputText: output,
            state: 'completed',
          })
          break
        }
        case 'codeError': {
          const error =
            entry.error ?? _codeInterpreterResultsAsText(entry.results)
          const idx = codeIdxByTrace.get(entry.traceId)
          const execution = idx === undefined ? undefined : steps[idx]
          if (execution?.kind === 'tool') {
            execution.error = _appendText(execution.error, error)
            execution.outputLabel = 'Error'
            execution.state = 'failed'
            break
          }
          steps.push({
            error,
            headerLabel: 'Code execution',
            kind: 'tool',
            outputLabel: 'Error',
            state: 'failed',
          })
          break
        }
        case 'citation':
          steps.push({
            kind: 'citation',
            text: entry.text,
            references: entry.references,
          })
          break
        case 'file':
          steps.push({ kind: 'file', name: entry.name })
          break
        case 'error':
          steps.push({ kind: 'error', message: entry.message })
          break
      }
    }

    // Once the overall message is no longer streaming, any tool step still
    // marked `invoking` (and any status still marked `active`) will never
    // get a follow-up event from the backend - e.g. RETURN_CONTROL action
    // groups don't emit a `toolResult` trace. Resolve them so the UI
    // doesn't show a perpetual spinner.
    if (messageStatus !== 'pending') {
      const fallbackToolState =
        messageStatus === 'failed' ? 'failed' : 'completed'
      for (const step of steps) {
        if (step.kind === 'tool' && step.state === 'invoking') {
          step.state = fallbackToolState
        } else if (step.kind === 'status' && step.state === 'active') {
          step.state = 'done'
        }
      }
    }

    // Model invocation statuses are useful as a live activity indicator, but
    // completed ones are implementation-level Bedrock cycles rather than
    // user-meaningful reasoning steps. Drop them once complete so a response
    // with many model/knowledge-base cycles does not end with dozens of
    // identical "Processing complete" rows.
    const deduped: DisplayStep[] = []
    for (const step of steps) {
      const prev = deduped[deduped.length - 1]
      const isPostToolStatusNoise =
        step.kind === 'status' && prev?.kind === 'tool'
      const isCompletedModelStatus =
        step.kind === 'status' && step.state === 'done'
      if (isPostToolStatusNoise || isCompletedModelStatus) {
        continue
      }
      deduped.push(step)
    }

    // If the message is still streaming and the timeline doesn't already
    // end with something visibly in-flight, append a synthetic "Thinking"
    // row so the user always has a live spinner while the agent is working.
    if (messageStatus === 'pending') {
      const last = deduped[deduped.length - 1]
      const alreadyShowsActivity =
        (last?.kind === 'tool' && last.state === 'invoking') ||
        (last?.kind === 'status' && last.state === 'active')
      if (!alreadyShowsActivity) {
        deduped.push({ kind: 'status', label: 'Thinking', state: 'active' })
      }
    }

    return deduped
  }

  /** Stand in for the raw failure detail clients do not receive. */
  const _CODE_FAILURE_NOTICE = 'Code execution failed.'
  const _TOOL_FAILURE_NOTICE = 'Tool execution failed.'

  /**
   * Reduces an {@link Info} entry to what a client needs to render it, so a
   * failure reports that it failed without shipping the trace or interpreter
   * dump behind it. The model's own {@link Rationale} explains what broke, and
   * it arrives as a reasoning step of its own.
   *
   * `toolResult.output` stays even on failure, being read for the tool's
   * structured response and retry count. The agent's next turn is unaffected,
   * reading what was persisted before this runs.
   */
  export function toClientSafe(entry: Info): Info {
    switch (entry.type) {
      case 'codeError':
        return {
          createdAt: entry.createdAt,
          error: _CODE_FAILURE_NOTICE,
          traceId: entry.traceId,
          type: 'codeError',
        }
      case 'toolResult':
        return entry.error === undefined
          ? entry
          : { ...entry, error: _TOOL_FAILURE_NOTICE }
      default:
        return entry
    }
  }

  /**
   * Returns a human-readable label for a display step when it represents
   * still-in-flight work, or `undefined` when the step has finished and
   * shouldn't be advertised as "current".
   */
  export function describeCurrentStep(step: DisplayStep): string | undefined {
    switch (step.kind) {
      case 'tool':
        if (step.state !== 'invoking') {
          return undefined
        }
        return step.headerOperation
          ? `${step.headerLabel} - ${step.headerOperation}`
          : step.headerLabel
      case 'status':
        return step.state === 'active' ? step.label : undefined
      case 'rationale':
      case 'text':
        // Surface the first non-empty line so long thoughts don't blow out
        // the trigger row.
        return step.text
          .split('\n')
          .find(line => line.trim().length > 0)
          ?.trim()
      case 'citation':
      case 'file':
      case 'error':
        return undefined
    }
  }

  function _formatPayload(value: unknown): string | undefined {
    if (value === undefined || value === null) {
      return undefined
    }
    if (TypeChecker.isString(value) && value.trim() === '') {
      return undefined
    }
    const parsed = TypeChecker.isString(value) ? _tryParseJson(value) : value
    if (parsed === undefined) {
      return TypeChecker.isString(value) ? value : undefined
    }
    try {
      return JSON.stringify(_unwrapEmbeddedJson(parsed), null, 2)
    } catch {
      if (TypeChecker.isString(value)) {
        return value
      }
      try {
        return JSON.stringify(value)
      } catch {
        return undefined
      }
    }
  }

  /** Joins streamed execution output without adding empty separators. */
  function _appendText(current: string | undefined, next: string): string {
    return [current, next].filter(Boolean).join('\n')
  }

  /** Converts raw interpreter results only when constructing display data. */
  function _codeInterpreterResultsAsText(
    results: readonly CodeInterpreterResult[] | undefined,
  ): string {
    return (results ?? [])
      .flatMap(result => [
        ...(result.content ?? []).map(
          block => block.text ?? block.resource?.text,
        ),
        result.structuredContent?.stdout,
        result.structuredContent?.stderr,
      ])
      .filter((value): value is string => Boolean(value))
      .join('\n')
  }

  function _composeToolHeader(
    toolName: string,
    actionGroup: string | undefined,
    apiPath: string | undefined,
  ): { headerLabel: string; headerOperation?: string } {
    const stripped = apiPath?.replace(/^\/+/, '')
    const operation =
      stripped !== undefined && stripped !== '' ? stripped : undefined
    const primary = operation ?? toolName
    const secondary =
      actionGroup && actionGroup !== primary ? actionGroup : undefined
    return {
      headerLabel: _humanize(primary),
      headerOperation: secondary ? _humanize(secondary) : undefined,
    }
  }

  function _humanize(value: string): string {
    if (!value) {
      return value
    }
    try {
      return getWords(value).map(capitalize).join(' ')
    } catch {
      return value
    }
  }

  function _tryParseJson(text: string): unknown {
    const trimmed = text.trim()
    if (!trimmed) {
      return undefined
    }
    const first = trimmed[0]
    if (first !== '{' && first !== '[' && first !== '"') {
      return text
    }
    try {
      return JSON.parse(trimmed)
    } catch {
      return text
    }
  }

  function _parsePayload(value: unknown): unknown {
    if (value === undefined || value === null) {
      return undefined
    }
    if (TypeChecker.isString(value)) {
      if (value.trim() === '') {
        return undefined
      }
      const parsed = _tryParseJson(value)
      // `_tryParseJson` returns the original string on failure - treat that
      // as "not JSON" and let the caller fall back to the text view.
      if (parsed === value) {
        return undefined
      }
      return _unwrapEmbeddedJson(parsed)
    }
    return _unwrapEmbeddedJson(value)
  }

  function _unwrapEmbeddedJson(value: unknown): unknown {
    if (TypeChecker.isString(value)) {
      const parsed = _tryParseJson(value)
      return parsed === value ? value : _unwrapEmbeddedJson(parsed)
    }
    if (TypeChecker.isReadonlyArray(value)) {
      return value.map(_unwrapEmbeddedJson)
    }
    if (TypeChecker.isObject(value) && !TypeChecker.isReadonlyArray(value)) {
      const out: Record<string, unknown> = {}
      for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
        out[k] = _unwrapEmbeddedJson(v)
      }
      return out
    }
    return value
  }

  function _extractEmbeddedError(value: unknown): string | undefined {
    if (value === undefined || value === null) {
      return undefined
    }
    const parsed = TypeChecker.isString(value) ? _tryParseJson(value) : value
    const unwrapped = _unwrapEmbeddedJson(parsed)
    if (
      !TypeChecker.isObject(unwrapped) ||
      TypeChecker.isReadonlyArray(unwrapped)
    ) {
      return undefined
    }

    const toolResult = (unwrapped as Record<string, unknown>).toolResult
    if (
      !TypeChecker.isObject(toolResult) ||
      TypeChecker.isReadonlyArray(toolResult) ||
      (toolResult as Record<string, unknown>).status !== 'ERROR'
    ) {
      return undefined
    }

    const message = (toolResult as Record<string, unknown>).message

    if (TypeChecker.isString(message)) {
      return message
    }
    return 'Tool returned status ERROR.'
  }

  function _extractRetryInfo(
    value: unknown,
  ): { attempt: number; max: number } | undefined {
    if (value === undefined || value === null) {
      return undefined
    }
    const parsed = TypeChecker.isString(value) ? _tryParseJson(value) : value
    const unwrapped = _unwrapEmbeddedJson(parsed)
    if (
      !TypeChecker.isObject(unwrapped) ||
      TypeChecker.isReadonlyArray(unwrapped)
    ) {
      return undefined
    }
    const unwrappedRecord = unwrapped as Record<string, unknown>
    const attempt = TypeChecker.isNumber(unwrappedRecord.retryAttempt)
      ? unwrappedRecord.retryAttempt
      : undefined
    const max = TypeChecker.isNumber(unwrappedRecord.maxRetries)
      ? unwrappedRecord.maxRetries
      : undefined
    if (attempt === undefined || max === undefined || max <= 0) {
      return undefined
    }
    return { attempt, max }
  }

  export type Reasoning = Exclude<Info, { type: 'text' | 'citation' | 'file' }>
  export namespace Reasoning {
    export const isReasoning = TypeChecker.Factory.custom(
      (entry: Info): entry is Reasoning =>
        !['text', 'citation', 'file'].includes(entry.type),
      { typeDescription: 'Info.Reasoning' },
    )
  }
}
